/* eslint-disable react-refresh/only-export-components -- route table, not a
   view module: 137 lazy handles plus the ROUTES array. Fast-refresh
   granularity is meaningless here (the router singleton remounts on any
   route change regardless), and splitting the table across files to
   please the heuristic would cost readability for zero runtime gain. */
import { lazy, Suspense } from 'react'
import { Outlet } from 'react-router-dom'
import ProtectedRoute from './components/ProtectedRoute'
import AppShell from './components/AppShell'
import { PERMS } from './lib/permissions'
import type { RouteEntry } from './lib/routeConfig'

// Route-entry types + the ROUTES → RouteObject mapper (the
// ROUTE_PERMISSION compile-time guard) live in ./lib/routeConfig.

// Layout route: hosts the Suspense boundary for lazy-loaded route elements.
function RootLayout() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  )
}

// Loading component
const LoadingScreen = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="text-center">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
)

// 404 Not Found component
function NotFound() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-primary mb-4">404</h1>
        <p className="text-xl text-muted-foreground mb-6">Page not found</p>
        <a
          href="/dashboard"
          className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Back to Dashboard
        </a>
      </div>
    </div>
  )
}

// Layout
const PatientChartLayout = lazy(() => import('./components/PatientChartLayout'))
const PortalLayout = lazy(() => import('./components/PortalLayout'))

// Existing pages
const Login = lazy(() => import('./pages/Login'))
const Setup = lazy(() => import('./pages/Setup'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const PatientRegistration = lazy(() => import('./pages/PatientRegistration'))
const EntityResolver = lazy(() => import('./pages/EntityResolver'))
const Handoffs = lazy(() => import('./pages/Handoffs'))
const PatientSpecimens = lazy(() => import('./pages/PatientSpecimens'))
const OrderEntry = lazy(() => import('./pages/OrderEntry'))
const ResultsReview = lazy(() => import('./pages/ResultsReview'))
const MedicationMAR = lazy(() => import('./pages/MedicationMAR'))
const MedicationOrderForm = lazy(() => import('./pages/MedicationOrderForm'))
const ClinicalNotes = lazy(() => import('./pages/ClinicalNotes'))
const Appointments = lazy(() => import('./pages/Appointments'))
const AppointmentForm = lazy(() => import('./pages/AppointmentForm'))

// Patient module pages
const PatientSearch = lazy(() => import('./pages/PatientSearch'))
const MyPatients = lazy(() => import('./pages/MyPatients'))
const PatientMerge = lazy(() => import('./pages/PatientMerge'))
const PatientDuplicates = lazy(() => import('./pages/PatientDuplicates'))
const PatientDemographics = lazy(() => import('./pages/PatientDemographics'))
const PatientEncounters = lazy(() => import('./pages/PatientEncounters'))
const PatientAllergies = lazy(() => import('./pages/PatientAllergies'))
const PatientProblems = lazy(() => import('./pages/PatientProblems'))
const PatientImmunizations = lazy(() => import('./pages/PatientImmunizations'))
const PatientInsurance = lazy(() => import('./pages/PatientInsurance'))
const PatientProxies = lazy(() => import('./pages/PatientProxies'))
const PatientAllergyForm = lazy(() => import('./pages/PatientAllergyForm'))
const PatientImplantableDevices = lazy(() => import('./pages/PatientImplantableDevices'))
const PatientCcdaImports = lazy(() => import('./pages/PatientCcdaImports'))
const PatientCcdaImportReview = lazy(() => import('./pages/PatientCcdaImportReview'))
const PatientImplantableDeviceForm = lazy(
  () => import('./pages/PatientImplantableDeviceForm'),
)
const PregnancyStatusForm = lazy(() => import('./pages/PregnancyStatusForm'))
const EncounterForm = lazy(() => import('./pages/EncounterForm'))
const PatientProblemForm = lazy(() => import('./pages/PatientProblemForm'))
const PatientImmunizationForm = lazy(
  () => import('./pages/PatientImmunizationForm'),
)
const PatientInsuranceForm = lazy(() => import('./pages/PatientInsuranceForm'))
const PatientContactForm = lazy(() => import('./pages/PatientContactForm'))
const IntakeOutput = lazy(() => import('./pages/IntakeOutput'))
const CarePlanManagement = lazy(() => import('./pages/CarePlanManagement'))
const Consents = lazy(() => import('./pages/Consents'))
const ConsentGrantForm = lazy(() => import('./pages/ConsentGrantForm'))
const FamilyHistoryTab = lazy(() => import('./pages/FamilyHistoryTab'))
const SocialHistoryTab = lazy(() => import('./pages/SocialHistoryTab'))
const SurgicalHistoryTab = lazy(() => import('./pages/SurgicalHistoryTab'))
const ReferralManagement = lazy(() => import('./pages/ReferralManagement'))
const AdvanceDirectivesTab = lazy(() => import('./pages/AdvanceDirectivesTab'))
const PatientIdentifiersTab = lazy(
  () => import('./pages/PatientIdentifiersTab'),
)
const PatientAddressesTab = lazy(() => import('./pages/PatientAddressesTab'))
const ProviderCoverage = lazy(() => import('./pages/ProviderCoverage'))

// Encounter module pages
const EncounterCensus = lazy(() => import('./pages/EncounterCensus'))
const EncounterDetail = lazy(() => import('./pages/EncounterDetail'))
const DischargeWorkflow = lazy(() => import('./pages/DischargeWorkflow'))

// Scheduling pages
const ScheduleManagement = lazy(() => import('./pages/ScheduleManagement'))
const Waitlist = lazy(() => import('./pages/Waitlist'))

// Clinical worklist pages
const OrderCatalog = lazy(() => import('./pages/OrderCatalog'))
const OrderSets = lazy(() => import('./pages/OrderSets'))
const OrdersWorklist = lazy(() => import('./pages/OrdersWorklist'))
const PendingResults = lazy(() => import('./pages/PendingResults'))
const CriticalResults = lazy(() => import('./pages/CriticalResults'))
const Formulary = lazy(() => import('./pages/Formulary'))
const MedicationWorklist = lazy(() => import('./pages/MedicationWorklist'))
const PharmacistQueue = lazy(() => import('./pages/PharmacistQueue'))
const MedReconciliation = lazy(() => import('./pages/MedReconciliation'))

// Documentation pages
const NotesWorklist = lazy(() => import('./pages/NotesWorklist'))
const NoteTemplates = lazy(() => import('./pages/NoteTemplates'))
const SmartPhrases = lazy(() => import('./pages/SmartPhrases'))

// Billing pages
const BillingCharges = lazy(() => import('./pages/BillingCharges'))
const BillingClaims = lazy(() => import('./pages/BillingClaims'))
const BillingPayments = lazy(() => import('./pages/BillingPayments'))
const PatientAccounts = lazy(() => import('./pages/PatientAccounts'))
const BillingStatements = lazy(() => import('./pages/BillingStatements'))

// Analytics pages
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'))
const ReportRunner = lazy(() => import('./pages/ReportRunner'))
const QualityMeasures = lazy(() => import('./pages/QualityMeasures'))
const QualityMeasureResultForm = lazy(() => import('./pages/QualityMeasureResultForm'))

// Admin pages
const AccessReviewDetail = lazy(() => import('./pages/AccessReviewDetail'))
const AccessReviews = lazy(() => import('./pages/AccessReviews'))
const AdminUsers = lazy(() => import('./pages/AdminUsers'))
const SsoAdmin = lazy(() => import('./pages/SsoAdmin'))
const UserGroups = lazy(() => import('./pages/UserGroups'))
const UserGroupForm = lazy(() => import('./pages/UserGroupForm'))
const SsoProviderForm = lazy(() => import('./pages/SsoProviderForm'))
const SsoDirectoryForm = lazy(() => import('./pages/SsoDirectoryForm'))
const SsoCallback = lazy(() => import('./pages/SsoCallback'))
const AdminFacilities = lazy(() => import('./pages/AdminFacilities'))
const AdminProviders = lazy(() => import('./pages/AdminProviders'))
const AdminIntegrations = lazy(() => import('./pages/AdminIntegrations'))
const AdminInterfaceMessageLog = lazy(
  () => import('./pages/AdminInterfaceMessageLog'),
)
const AdminAudit = lazy(() => import('./pages/AdminAudit'))
const AdminSystem = lazy(() => import('./pages/AdminSystem'))
const AdminEscalationRules = lazy(() => import('./pages/AdminEscalationRules'))
const AdminThirdPartyApps = lazy(() => import('./pages/AdminThirdPartyApps'))

// Portal pages
const PortalLogin = lazy(() => import('./pages/PortalLogin'))
const PortalRegister = lazy(() => import('./pages/PortalRegister'))
const PortalDashboard = lazy(() => import('./pages/PortalDashboard'))
const PortalResults = lazy(() => import('./pages/PortalResults'))
const PortalAppointments = lazy(() => import('./pages/PortalAppointments'))
const PortalMessages = lazy(() => import('./pages/PortalMessages'))
const PortalForms = lazy(() => import('./pages/PortalForms'))

// New pages
const AccessDenied = lazy(() => import('./pages/AccessDenied'))
const UserProfile = lazy(() => import('./pages/UserProfile'))
const NotificationInbox = lazy(() => import('./pages/NotificationInbox'))
const UnifiedInbox = lazy(() => import('./pages/UnifiedInbox'))
const VitalsEntry = lazy(() => import('./pages/VitalsEntry'))
const PatientContacts = lazy(() => import('./pages/PatientContacts'))
const ProviderDirectory = lazy(() => import('./pages/ProviderDirectory'))
const CdsRulesAdmin = lazy(() => import('./pages/CdsRulesAdmin'))
const OrderCosignQueue = lazy(() => import('./pages/OrderCosignQueue'))
const WoundCareDoc = lazy(() => import('./pages/WoundCareDoc'))
const NursingAssessmentForm = lazy(
  () => import('./pages/NursingAssessmentForm'),
)

// Licensing admin pages
const AccessMatrix = lazy(() => import('./pages/AccessMatrix'))
const AdminLicenseFileKey = lazy(() => import('./pages/AdminLicenseFileKey'))

// Admin form pages (modal-to-page conversions)
const AdminUserForm = lazy(() => import('./pages/AdminUserForm'))
const AdminProviderForm = lazy(() => import('./pages/AdminProviderForm'))
const AdminFacilityForm = lazy(() => import('./pages/AdminFacilityForm'))
const AdminHealthSystems = lazy(() => import('./pages/AdminHealthSystems'))
const AdminHealthSystemForm = lazy(
  () => import('./pages/AdminHealthSystemForm'),
)
const AdminTaxonomy = lazy(() => import('./pages/AdminTaxonomy'))
const AdminServiceLineForm = lazy(() => import('./pages/AdminServiceLineForm'))
const AdminClinicalServiceForm = lazy(
  () => import('./pages/AdminClinicalServiceForm'),
)
const AdminServiceLocations = lazy(
  () => import('./pages/AdminServiceLocations'),
)
const AdminDepartmentForm = lazy(() => import('./pages/AdminDepartmentForm'))
const AdminLocations = lazy(() => import('./pages/AdminLocations'))
const AdminLocationForm = lazy(() => import('./pages/AdminLocationForm'))
const AdminIntegrationForm = lazy(() => import('./pages/AdminIntegrationForm'))
const AdminSmartAppForm = lazy(() => import('./pages/AdminSmartAppForm'))
const AdminFhirEndpointForm = lazy(
  () => import('./pages/AdminFhirEndpointForm'),
)
const CdsRuleForm = lazy(() => import('./pages/CdsRuleForm'))
const RoleForm = lazy(() => import('./pages/RoleForm'))
const CategoryForm = lazy(() => import('./pages/CategoryForm'))
const RolePermissionsForm = lazy(() => import('./pages/RolePermissionsForm'))
const RoleDeniedPermissionsForm = lazy(
  () => import('./pages/RoleDeniedPermissionsForm'),
)
const CategoryPermissionsForm = lazy(
  () => import('./pages/CategoryPermissionsForm'),
)
const AdminOrgLicenseKey = lazy(() => import('./pages/AdminOrgLicenseKey'))
const NoteTemplateForm = lazy(() => import('./pages/NoteTemplateForm'))
const SmartPhraseForm = lazy(() => import('./pages/SmartPhraseForm'))
const ScheduleTemplateForm = lazy(() => import('./pages/ScheduleTemplateForm'))
const SchedulingCheckInForm = lazy(
  () => import('./pages/SchedulingCheckInForm'),
)
const BillingChargeForm = lazy(() => import('./pages/BillingChargeForm'))
const BillingPaymentForm = lazy(() => import('./pages/BillingPaymentForm'))
const BillingClaimForm = lazy(() => import('./pages/BillingClaimForm'))
const DenialManagement = lazy(() => import('./pages/DenialManagement'))

// The route tree. Built into the router once at module load in App.tsx.
// Entries reference the lazy components above, so keep this table below
// them (module order, not hoisting: a `const` used before its line
// TDZ-throws at import time = a blank app root).
export const ROUTES: RouteEntry[] = [
  {
    element: <RootLayout />,
    children: [
      /* Public routes */
      { path: '/login', publicReason: 'front-door', element: <Login /> },
      /* First-run setup: the install has no accounts yet, so no session
         can exist — public by construction, like the front door itself. */
      { path: '/setup', publicReason: 'front-door', element: <Setup /> },
      /* SSO callback catcher — the IdP redirects here; public
                      because the browser arrives without a session. */
      {
        path: '/sso/callback',
        publicReason: 'front-door',
        element: <SsoCallback />,
      },

      /* Protected routes — require authentication, then wrapped in AppShell */
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppShell />,
            children: [
              /* Dashboard */
              {
                path: '/dashboard',
                publicReason: 'user-scoped',
                element: <Dashboard />,
              },

              /* Profile & Notifications */
              {
                path: '/profile',
                publicReason: 'user-scoped',
                element: <UserProfile />,
              },
              {
                path: '/notifications',
                publicReason: 'user-scoped',
                element: <NotificationInbox />,
              },

              /* Patient module */
              {
                path: '/patients/search',
                permission: PERMS.PATIENT_VIEW,
                element: <PatientSearch />,
              },
              {
                path: '/patients/my',
                permission: PERMS.PATIENT_VIEW,
                element: <MyPatients />,
              },
              {
                path: '/patients/register',
                permission: PERMS.PATIENT_CREATE,
                element: <PatientRegistration />,
              },
              {
                path: '/patients/merge',
                permission: PERMS.PATIENT_MERGE,
                element: <PatientMerge />,
              },
              {
                path: '/patients/duplicates',
                permission: PERMS.PATIENT_MERGE,
                element: <PatientDuplicates />,
              },

              /* Patient chart with shared layout (banner + tab nav).
                        Least-privilege: the whole chart subtree requires
                        PATIENT_VIEW — roles without it (system administrators)
                        land on /403 with the attempted path + reason instead
                        of a chart that would only 403 per-panel. */
              {
                path: '/patients/:patientId',
                permission: PERMS.PATIENT_VIEW,
                element: <PatientChartLayout />,
                children: [
                  { index: true, element: <PatientDemographics /> },
                  { path: 'demographics', element: <PatientDemographics /> },
                  { path: 'problems', element: <PatientProblems /> },
                  { path: 'allergies', element: <PatientAllergies /> },
                  { path: 'implantable-devices', element: <PatientImplantableDevices /> },
                  { path: 'ccda-imports', element: <PatientCcdaImports /> },
                  { path: 'ccda-imports/:importId', element: <PatientCcdaImportReview /> },
                  /* No RequirePermission wrapper: the page is PATIENT_VIEW
                          readable and gates its own record button, matching
                          PatientAllergies. */
                  {
                    path: 'pregnancy-status',
                    element: <PregnancyStatusForm />,
                  },
                  { path: 'encounters', element: <PatientEncounters /> },
                  { path: 'orders', element: <OrderEntry /> },
                  { path: 'results', element: <ResultsReview /> },
                  { path: 'medications', element: <MedicationMAR /> },
                  {
                    path: 'medications/new',
                    permission: PERMS.MED_PRESCRIBE,
                    element: <MedicationOrderForm />,
                  },
                  { path: 'notes', element: <ClinicalNotes /> },
                  { path: 'specimens', element: <PatientSpecimens /> },
                  { path: 'immunizations', element: <PatientImmunizations /> },
                  { path: 'insurance', element: <PatientInsurance /> },
                  { path: 'proxies', element: <PatientProxies /> },
                  { path: 'contacts', element: <PatientContacts /> },
                  { path: 'intake-output', element: <IntakeOutput /> },
                  { path: 'care-plans', element: <CarePlanManagement /> },
                  { path: 'consents', element: <Consents /> },
                  { path: 'consents/new', element: <ConsentGrantForm /> },
                  { path: 'family-history', element: <FamilyHistoryTab /> },
                  { path: 'social-history', element: <SocialHistoryTab /> },
                  { path: 'surgical-history', element: <SurgicalHistoryTab /> },
                  { path: 'referrals', element: <ReferralManagement /> },
                  {
                    path: 'advance-directives',
                    element: <AdvanceDirectivesTab />,
                  },
                  { path: 'identifiers', element: <PatientIdentifiersTab /> },
                  { path: 'addresses', element: <PatientAddressesTab /> },
                  { path: 'allergies/new', element: <PatientAllergyForm /> },
                  {
                    path: 'implantable-devices/new',
                    element: <PatientImplantableDeviceForm />,
                  },
                  {
                    path: 'implantable-devices/:deviceId/edit',
                    element: <PatientImplantableDeviceForm />,
                  },
                  { path: 'problems/new', element: <PatientProblemForm /> },
                  {
                    path: 'immunizations/new',
                    element: <PatientImmunizationForm />,
                  },
                  { path: 'insurance/new', element: <PatientInsuranceForm /> },
                  { path: 'contacts/new', element: <PatientContactForm /> },
                  { path: 'encounters/new', element: <EncounterForm /> },
                ],
              },

              /* Encounter module */
              {
                path: '/encounters/census',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <EncounterCensus />,
              },
              {
                path: '/encounters/:encounterId',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <EncounterDetail />,
              },
              {
                path: '/encounters/:encounterId/discharge',
                permission: PERMS.ENCOUNTER_DISCHARGE,
                element: <DischargeWorkflow />,
              },
              {
                path: '/encounters/:encounterId/vitals',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <VitalsEntry />,
              },
              {
                path: '/encounters/:encounterId/wound-care',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <WoundCareDoc />,
              },
              {
                path: '/encounters/:encounterId/nursing-assessment',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <NursingAssessmentForm />,
              },

              /* Scheduling */
              {
                path: '/appointments',
                permission: PERMS.SCHEDULE_VIEW,
                element: <Appointments />,
              },
              {
                path: '/appointments/new',
                permission: PERMS.SCHEDULE_MANAGE,
                element: <AppointmentForm />,
              },
              {
                path: '/appointments/:id/edit',
                permission: PERMS.SCHEDULE_MANAGE,
                element: <AppointmentForm />,
              },
              {
                path: '/appointments/schedule',
                permission: PERMS.SCHEDULE_VIEW,
                element: <ScheduleManagement />,
              },
              {
                path: '/appointments/:id/check-in',
                permission: PERMS.SCHEDULE_MANAGE,
                element: <SchedulingCheckInForm />,
              },
              {
                path: '/appointments/schedule/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <ScheduleTemplateForm />,
              },
              {
                path: '/appointments/schedule/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <ScheduleTemplateForm />,
              },
              {
                path: '/appointments/waitlist',
                permission: PERMS.SCHEDULE_VIEW,
                element: <Waitlist />,
              },

              /* Providers */
              {
                path: '/providers',
                permission: PERMS.PATIENT_VIEW,
                element: <ProviderDirectory />,
              },
              {
                path: '/coverage',
                publicReason: 'user-scoped',
                element: <ProviderCoverage />,
              },

              /* Clinical worklists */
              {
                path: '/inbox',
                publicReason: 'aggregator',
                element: <UnifiedInbox />,
              },
              {
                path: '/orders/catalog',
                permission: PERMS.ORDER_VIEW,
                element: <OrderCatalog />,
              },
              {
                path: '/orders/sets',
                permission: PERMS.ORDER_VIEW,
                element: <OrderSets />,
              },
              {
                path: '/orders/cosign-queue',
                permission: PERMS.ORDER_COSIGN,
                element: <OrderCosignQueue />,
              },
              {
                path: '/orders/worklist',
                permission: PERMS.ORDER_VIEW,
                element: <OrdersWorklist />,
              },
              {
                path: '/results/pending',
                permission: PERMS.ORDER_VIEW,
                element: <PendingResults />,
              },
              {
                path: '/results/critical',
                permission: PERMS.RESULT_VIEW,
                element: <CriticalResults />,
              },
              {
                path: '/medications/formulary',
                permission: PERMS.MED_VIEW,
                element: <Formulary />,
              },
              {
                path: '/medications/worklist',
                permission: PERMS.MED_VIEW,
                element: <MedicationWorklist />,
              },
              {
                path: '/medications/pharmacist-queue',
                permission: PERMS.MED_VIEW,
                element: <PharmacistQueue />,
              },
              {
                path: '/patients/:patientId/encounters/:encounterId/reconciliation',
                permission: PERMS.MED_RECONCILE,
                element: <MedReconciliation />,
              },

              /* Documentation */
              {
                path: '/notes/worklist',
                permission: PERMS.DOC_VIEW,
                element: <NotesWorklist />,
              },
              {
                path: '/notes/templates',
                permission: PERMS.DOC_VIEW,
                element: <NoteTemplates />,
              },
              {
                path: '/notes/templates/new',
                permission: PERMS.DOC_CREATE,
                element: <NoteTemplateForm />,
              },
              {
                path: '/notes/templates/:id/edit',
                permission: PERMS.DOC_CREATE,
                element: <NoteTemplateForm />,
              },
              {
                path: '/notes/smart-phrases',
                permission: PERMS.DOC_VIEW,
                element: <SmartPhrases />,
              },
              {
                path: '/notes/smart-phrases/new',
                permission: PERMS.DOC_CREATE,
                element: <SmartPhraseForm />,
              },
              {
                path: '/notes/smart-phrases/:id/edit',
                permission: PERMS.DOC_CREATE,
                element: <SmartPhraseForm />,
              },

              /* Permalinks for chart-resident entities. Each resolves the
                        entity's patient, then redirects into the chart tab with
                        ?focus=<id>. Declared after the static worklist paths above;
                        React Router ranks static segments over dynamic ones, so
                        /results/critical still wins over /results/:id. */
              /* I-PASS handoffs: the receiving clinician's inbox. The feature
                        was fully built and had no page at all — handoffs were written
                        and never read. */
              {
                path: '/handoffs',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <Handoffs />,
              },

              {
                path: '/results/:id',
                permission: PERMS.RESULT_VIEW,
                element: <EntityResolver kind="result" />,
              },
              {
                path: '/orders/:id',
                permission: PERMS.ORDER_VIEW,
                element: <EntityResolver kind="order" />,
              },
              {
                path: '/medications/:id',
                permission: PERMS.MED_VIEW,
                element: <EntityResolver kind="medication" />,
              },
              {
                path: '/notes/:id',
                permission: PERMS.DOC_VIEW,
                element: <EntityResolver kind="note" />,
              },
              {
                path: '/specimens/:id',
                permission: PERMS.ORDER_VIEW,
                element: <EntityResolver kind="specimen" />,
              },
              {
                path: '/handoffs/:id',
                permission: PERMS.ENCOUNTER_VIEW,
                element: <EntityResolver kind="handoff" />,
              },
              /* Appointments were edit-only: you could permalink the edit form
                        but not the appointment. */
              {
                path: '/appointments/:id',
                permission: PERMS.SCHEDULE_VIEW,
                element: <EntityResolver kind="appointment" />,
              },

              /* Billing */
              {
                path: '/billing/charges',
                permission: PERMS.BILLING_VIEW,
                element: <BillingCharges />,
              },
              {
                path: '/billing/charges/new',
                permission: PERMS.BILLING_CREATE,
                element: <BillingChargeForm />,
              },
              {
                path: '/billing/claims',
                permission: PERMS.BILLING_VIEW,
                element: <BillingClaims />,
              },
              {
                path: '/billing/claims/new',
                permission: PERMS.BILLING_CREATE,
                element: <BillingClaimForm />,
              },
              {
                path: '/billing/payments',
                permission: PERMS.BILLING_VIEW,
                element: <BillingPayments />,
              },
              {
                path: '/billing/payments/new',
                permission: PERMS.BILLING_PAYMENT,
                element: <BillingPaymentForm />,
              },
              {
                path: '/billing/accounts',
                permission: PERMS.BILLING_VIEW,
                element: <PatientAccounts />,
              },
              {
                path: '/billing/statements',
                permission: PERMS.BILLING_VIEW,
                element: <BillingStatements />,
              },
              {
                path: '/billing/denials',
                permission: PERMS.BILLING_VIEW,
                element: <DenialManagement />,
              },

              /* Analytics */
              {
                path: '/analytics/dashboard',
                permission: PERMS.ANALYTICS_VIEW,
                element: <AnalyticsDashboard />,
              },
              {
                path: '/analytics/reports',
                permission: PERMS.ANALYTICS_VIEW,
                element: <ReportRunner />,
              },
              {
                path: '/analytics/quality',
                permission: PERMS.ANALYTICS_VIEW,
                element: <QualityMeasures />,
              },
              // Recording is SYS_ADMIN_ALL server-side
              // (record_quality_measure_result); the route matches.
              // NB path stays the first prop: check-route-coverage only
              // reads the entry's opening lines.
              {
                path: '/analytics/quality/:measureId/record',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <QualityMeasureResultForm />,
              },

              /* Administration — all admin routes require USER_MANAGE or SYS_ADMIN_ALL */
              {
                path: '/admin/users',
                permission: PERMS.USER_MANAGE,
                element: <AdminUsers />,
              },
              {
                path: '/admin/users/new',
                permission: PERMS.USER_MANAGE,
                element: <AdminUserForm />,
              },
              {
                path: '/admin/users/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <AdminUserForm />,
              },
              {
                path: '/admin/access-reviews',
                permission: PERMS.USER_MANAGE,
                element: <AccessReviews />,
              },
              {
                path: '/admin/access-reviews/:id',
                permission: PERMS.USER_MANAGE,
                element: <AccessReviewDetail />,
              },
              /* SSO administration (S4) */
              {
                path: '/admin/sso',
                permission: PERMS.USER_MANAGE,
                element: <SsoAdmin />,
              },
              {
                path: '/admin/sso/providers/new',
                permission: PERMS.USER_MANAGE,
                element: <SsoProviderForm />,
              },
              {
                path: '/admin/sso/providers/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <SsoProviderForm />,
              },
              {
                path: '/admin/sso/directories/new',
                permission: PERMS.USER_MANAGE,
                element: <SsoDirectoryForm />,
              },
              {
                path: '/admin/sso/directories/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <SsoDirectoryForm />,
              },
              /* User groups (S6a) */
              {
                path: '/admin/groups',
                permission: PERMS.USER_MANAGE,
                element: <UserGroups />,
              },
              {
                path: '/admin/groups/new',
                permission: PERMS.USER_MANAGE,
                element: <UserGroupForm />,
              },
              {
                path: '/admin/groups/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <UserGroupForm />,
              },
              /* Retired standalone roles page — redirect to the access-matrix roles tab. */
              {
                path: '/admin/roles',
                permission: PERMS.USER_MANAGE,
                redirectTo: '/admin/access-matrix?tab=roles',
              },
              {
                path: '/admin/facilities',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminFacilities />,
              },
              {
                path: '/admin/facilities/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminFacilityForm />,
              },
              {
                path: '/admin/facilities/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminFacilityForm />,
              },
              {
                path: '/admin/health-systems',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminHealthSystems />,
              },
              {
                path: '/admin/health-systems/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminHealthSystemForm />,
              },
              {
                path: '/admin/health-systems/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminHealthSystemForm />,
              },
              {
                path: '/admin/taxonomy',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminTaxonomy />,
              },
              {
                path: '/admin/taxonomy/service-lines/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminServiceLineForm />,
              },
              {
                path: '/admin/taxonomy/service-lines/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminServiceLineForm />,
              },
              {
                path: '/admin/taxonomy/clinical-services/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminClinicalServiceForm />,
              },
              {
                path: '/admin/taxonomy/clinical-services/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminClinicalServiceForm />,
              },
              {
                path: '/admin/service-locations',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminServiceLocations />,
              },
              {
                path: '/admin/departments/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminDepartmentForm />,
              },
              {
                path: '/admin/departments/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminDepartmentForm />,
              },
              {
                path: '/admin/locations',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminLocations />,
              },
              {
                path: '/admin/locations/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminLocationForm />,
              },
              {
                path: '/admin/locations/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminLocationForm />,
              },
              {
                path: '/admin/providers',
                permission: PERMS.USER_MANAGE,
                element: <AdminProviders />,
              },
              {
                path: '/admin/providers/new',
                permission: PERMS.USER_MANAGE,
                element: <AdminProviderForm />,
              },
              {
                path: '/admin/providers/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <AdminProviderForm />,
              },
              {
                path: '/admin/integrations',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminIntegrations />,
              },
              {
                path: '/admin/integrations/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminIntegrationForm />,
              },
              {
                path: '/admin/integrations/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminIntegrationForm />,
              },
              {
                path: '/admin/integrations/smart-apps/new',
                permission: PERMS.USER_MANAGE,
                element: <AdminSmartAppForm />,
              },
              {
                path: '/admin/integrations/smart-apps/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <AdminSmartAppForm />,
              },
              {
                path: '/admin/integrations/fhir-endpoints/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminFhirEndpointForm />,
              },
              {
                path: '/admin/integrations/fhir-endpoints/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminFhirEndpointForm />,
              },
              {
                path: '/admin/interfaces/messages',
                permission: PERMS.AUDIT_VIEW,
                element: <AdminInterfaceMessageLog />,
              },
              {
                path: '/admin/audit',
                permission: PERMS.AUDIT_VIEW,
                element: <AdminAudit />,
              },
              {
                path: '/admin/system',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminSystem />,
              },
              {
                path: '/admin/escalation-rules',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminEscalationRules />,
              },
              {
                path: '/admin/third-party-apps',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <AdminThirdPartyApps />,
              },
              {
                path: '/admin/access-matrix',
                permission: PERMS.USER_MANAGE,
                element: <AccessMatrix />,
              },
              {
                path: '/admin/access-matrix/roles/new',
                permission: PERMS.USER_MANAGE,
                element: <RoleForm />,
              },
              {
                path: '/admin/access-matrix/roles/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <RoleForm />,
              },
              {
                path: '/admin/access-matrix/roles/:id/permissions',
                permission: PERMS.USER_MANAGE,
                element: <RolePermissionsForm />,
              },
              {
                path: '/admin/access-matrix/roles/:id/denied',
                permission: PERMS.USER_MANAGE,
                element: <RoleDeniedPermissionsForm />,
              },
              {
                path: '/admin/access-matrix/categories/new',
                permission: PERMS.USER_MANAGE,
                element: <CategoryForm />,
              },
              {
                path: '/admin/access-matrix/categories/:id/edit',
                permission: PERMS.USER_MANAGE,
                element: <CategoryForm />,
              },
              {
                path: '/admin/access-matrix/categories/:id/permissions',
                permission: PERMS.USER_MANAGE,
                element: <CategoryPermissionsForm />,
              },
              {
                path: '/admin/cds-rules',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <CdsRulesAdmin />,
              },
              {
                path: '/admin/cds-rules/new',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <CdsRuleForm />,
              },
              {
                path: '/admin/cds-rules/:id/edit',
                permission: PERMS.SYS_ADMIN_ALL,
                element: <CdsRuleForm />,
              },

              /* Licensing Administration */
              {
                path: '/admin/licensing/file-key',
                permission: PERMS.LICENSING_MANAGE,
                selfManagedOnly: true,
                element: <AdminLicenseFileKey />,
              },
              {
                path: '/admin/licensing/orgs/:healthSystemId/key',
                permission: PERMS.LICENSING_MANAGE,
                element: <AdminOrgLicenseKey />,
              },
            ],
          },
        ],
      },

      /* Portal Routes - separate from main EMR */
      {
        path: '/portal/login',
        publicReason: 'portal-auth',
        element: <PortalLogin />,
      },
      {
        path: '/portal/register',
        publicReason: 'portal-auth',
        element: <PortalRegister />,
      },
      {
        path: '/portal',
        publicReason: 'portal-auth',
        element: <PortalLayout />,
        children: [
          { index: true, redirectTo: '/portal/dashboard' },
          { path: 'dashboard', element: <PortalDashboard /> },
          { path: 'results', element: <PortalResults /> },
          { path: 'appointments', element: <PortalAppointments /> },
          { path: 'messages', element: <PortalMessages /> },
          { path: 'forms', element: <PortalForms /> },
        ],
      },

      /* Default redirect */
      { path: '/', redirectTo: '/dashboard' },

      /* 403 Access Denied — target of RequirePermission redirects */
      {
        path: '/403',
        publicReason: 'access-denied',
        element: <AccessDenied />,
      },

      /* 404 Not Found */
      { path: '*', publicReason: 'not-found', element: <NotFound /> },
    ],
  },
]
